"use client";

import { Check, ChevronsUpDown, Settings } from "lucide-react";
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../b37/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export interface Organization {
  id: string;
  name: string;
  /** User's role in this organisation — used to gate the org settings gear icon */
  role?: string | null;
  type?: string;
}

export interface OrgSwitcherProps {
  currentOrg?: Organization;
  onOrgChange?: (org: Organization) => void;
  organizations: Organization[];
  /** href for the org settings page — shows a gear icon for team_manager and account_manager roles */
  orgSettingsHref?: string;
}

const ORG_ADMIN_ROLES = new Set(["account_manager", "team_manager"]);

export function OrgSwitcher({
  organizations,
  currentOrg,
  onOrgChange,
  orgSettingsHref,
}: OrgSwitcherProps) {
  const { isMobile } = useSidebar();

  if (!currentOrg && organizations.length > 0) {
    return null;
  }

  const canSeeOrgSettings =
    orgSettingsHref !== undefined &&
    currentOrg?.role != null &&
    ORG_ADMIN_ROLES.has(currentOrg.role);

  return (
    <SidebarMenu className="group-data-[collapsible=icon]:hidden">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              size="lg"
            >
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {currentOrg?.name || "Select organization"}
                </span>
                {currentOrg?.type ? (
                  <span className="truncate text-xs">{currentOrg.type}</span>
                ) : null}
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel>Organizations</DropdownMenuLabel>
            {organizations.map((org) => (
              <DropdownMenuItem key={org.id} onClick={() => onOrgChange?.(org)}>
                <div className="flex flex-1 flex-col">
                  <span className="font-medium text-sm">{org.name}</span>
                  {org.type ? (
                    <span className="text-muted-foreground text-xs">
                      {org.type}
                    </span>
                  ) : null}
                </div>
                {currentOrg?.id === org.id ? (
                  <Check className="h-4 w-4" />
                ) : null}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
        {canSeeOrgSettings ? (
          <SidebarMenuAction asChild showOnHover>
            <a href={orgSettingsHref}>
              <Settings />
              <span className="sr-only">Organisation settings</span>
            </a>
          </SidebarMenuAction>
        ) : null}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
